<template>
  <ModalComp
    :is-visible="isVisible"
    title="Compte grossiste"
    size="lg"
    hide-footer
    @update:is-visible="emit('update:isVisible', $event)"
  >
    <template #body>
      <div v-if="detailsLoading" class="py-10 text-center text-gray-500">
        <i class="fas fa-spinner fa-spin text-2xl mb-3"></i>
        <p>Chargement…</p>
      </div>

      <div
        v-else-if="detailsError"
        class="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
      >
        {{ detailsError }}
      </div>

      <div v-else-if="details" class="space-y-4 text-sm">
        <dl class="grid grid-cols-2 gap-x-4 gap-y-2 text-gray-700">
          <dt class="text-gray-500">ID vendeur</dt>
          <dd class="font-mono text-xs">{{ details.id_vendeur }}</dd>
          <dt class="text-gray-500">Statut eFashion</dt>
          <dd>
            <span
              :class="
                details.statutEfashion === 'Validé' ||
                details.statutEfashion === 'Actif'
                  ? 'text-green-700 font-medium'
                  : 'text-amber-700 font-medium'
              "
            >
              {{ details.statutEfashion }}
            </span>
          </dd>
          <dt class="text-gray-500">Statut du compte</dt>
          <dd>
            <span
              :class="
                details.statutCompte === 'En ligne'
                  ? 'text-green-700 font-medium'
                  : 'text-amber-700 font-medium'
              "
            >
              {{ details.statutCompte }}
            </span>
          </dd>
          <dt class="text-gray-500">Date d'inscription</dt>
          <dd>{{ formatDateInscription(details.dateInscription) }}</dd>
          <dt class="text-gray-500">Prénom</dt>
          <dd>{{ details.prenomContact || '—' }}</dd>
          <dt class="text-gray-500">Nom</dt>
          <dd>{{ details.nomContact || '—' }}</dd>
          <dt class="text-gray-500">Email</dt>
          <dd class="break-all">{{ details.email || '—' }}</dd>
          <dt class="text-gray-500">Téléphone</dt>
          <dd>{{ details.telephone || '—' }}</dd>
          <dt class="text-gray-500">Adresse</dt>
          <dd class="col-span-1 whitespace-pre-wrap break-words">
            {{ details.adresse || '—' }}
          </dd>
          <dt class="text-gray-500">Boutique</dt>
          <dd class="font-medium text-gray-900">
            {{ details.nomBoutique || '—' }}
          </dd>
          <dt class="text-gray-500">Société</dt>
          <dd>{{ details.nomSociete || '—' }}</dd>
        </dl>

        <div class="flex flex-wrap justify-end gap-2 pt-2">
          <button
            type="button"
            class="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
            @click="emit('update:isVisible', false)"
          >
            Fermer
          </button>
          <button
            type="button"
            :disabled="accessLoading"
            class="px-4 py-2 text-sm rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            @click="accessAccount"
          >
            <i v-if="accessLoading" class="fas fa-spinner fa-spin mr-1"></i>
            <i v-else class="fas fa-sign-in-alt mr-1"></i>
            {{ accessLoading ? 'Connexion…' : 'Accéder à son compte' }}
          </button>
        </div>
      </div>
    </template>
  </ModalComp>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ModalComp } from '@/components'
import { adminEfpClearImpersonation } from '@/api/adminEfp'
import { useAdminEfpStore } from '@/stores/adminEfp'
import { useAuthStore } from '@/stores/auth'
import type { AdminEfpVendeurDetails } from '@/api/adminEfp'

const props = defineProps<{
  isVisible: boolean
  vendeurId: number | null
}>()

const emit = defineEmits<{
  'update:isVisible': [boolean]
}>()

const router = useRouter()
const adminEfp = useAdminEfpStore()
const authStore = useAuthStore()

const details = ref<AdminEfpVendeurDetails | null>(null)
const detailsLoading = ref(false)
const detailsError = ref('')
const accessLoading = ref(false)

function formatDateInscription(value?: string | null): string {
  if (!value) return '—'
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return value
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('fr-FR')
}

async function loadDetails(idVendeur: number) {
  details.value = null
  detailsError.value = ''
  detailsLoading.value = true
  try {
    details.value = await adminEfp.getVendeurDetails(idVendeur)
  } catch (e) {
    detailsError.value = e instanceof Error ? e.message : 'Erreur de chargement'
  } finally {
    detailsLoading.value = false
  }
}

async function accessAccount() {
  const d = details.value
  if (!d) return
  const popup = window.open('about:blank', '_blank')
  accessLoading.value = true
  detailsError.value = ''
  try {
    await adminEfpClearImpersonation()
    const { ok, adminDisplayName } = await adminEfp.impersonate(d.id_vendeur)
    if (!ok) throw new Error('Impersonation impossible')
    authStore.setImpersonationFromCookie(true, adminDisplayName ?? undefined)
    const dashboardUrl = router.resolve('/dashboard').href
    if (popup) {
      popup.opener = null
      popup.location.href = dashboardUrl
    } else {
      window.location.href = dashboardUrl
    }
    emit('update:isVisible', false)
  } catch (e) {
    popup?.close()
    detailsError.value = e instanceof Error ? e.message : 'Impersonation impossible'
  } finally {
    accessLoading.value = false
  }
}

watch(
  () => [props.isVisible, props.vendeurId] as const,
  ([visible, idVendeur]) => {
    if (visible && idVendeur) {
      void loadDetails(idVendeur)
      return
    }
    details.value = null
    detailsError.value = ''
    accessLoading.value = false
  },
)
</script>
